import { ActivityReference, Storey } from '@shared/models/design/storey.model';
import { Activity } from '@shared/models/design/activities/activity.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
// Import directly to avoid factory index circular dependency.
import { createActivityReference } from '../factories/activity-reference.factory';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { getId, replaceProperties } from '@shared/helpers/object.helpers';
import {
  redistributeActivityRefsShares,
  sortActivityRefsByType,
} from '../activity-ref.helpers';

/**
 * Add an activity reference to a storey by createing a new activity reference and applying it to the storey
 * @param storey Storey to add the activity reference to
 * @param type
 * @param share How much of the floor area is occupied by the activity.
 * @returns A modified storey with the new activity reference
 */
const addStoreyActivityReference = (
  storey: Storey,
  activity_id: Activity['id'],
  type: ActivityType,
  share: number = 1,
): Storey => {
  const newRef = createActivityReference(activity_id, type, share);
  return redistributeActivityRefsShares(
    {
      ...storey,
      activities: sortActivityRefsByType([...storey.activities, newRef]),
    },
    newRef.id,
  );
};

/**
 * Remove an activity reference from a storey
 * @param storey - The storey to remove the activity reference from
 * @param activityOrId - The activity or id of the activity to remove the reference from
 * @returns The new storey (or the original storey if nothing changed)
 */
const removeStoreyActivityReferences = (
  storey: Storey,
  ...activityOrIds: ItemOrItemId<Activity>[]
): Storey => {
  const ids = activityOrIds.map(getId);

  // TODO: Must recalculate share
  const updatedStorey = replaceProperties(storey, {
    activities: storey.activities.filter(
      (activity) => !ids.includes(activity.activity_id),
    ),
  });

  // Redistribute shares if any changes were made to the activity references
  if (updatedStorey.activities !== storey.activities) {
    return redistributeActivityRefsShares(updatedStorey);
  }
  return updatedStorey;
};

/**
 * Update an activity reference in a storey
 * @param storey Storey to update the activity reference in
 * @param update Activity reference to update
 * @returns A modified storey with the updated activity reference
 */
const updateStoreyActivityReference = (
  storey: Storey,
  ...updates: SemiPartial<ActivityReference, 'id'>[]
): Storey => {
  const ids = updates.map(getId);

  // Return updated storey with the updated activity reference (if any changes were made)

  const updatedStorey = replaceProperties(storey, {
    activities: storey.activities.map((activity) => {
      const update = updates.find((update) => update.id === activity.id);
      if (!update) return activity;
      return replaceProperties(activity, update);
    }),
  });

  // Redistribute shares if any changes were made to the activity references
  if (updatedStorey.activities !== storey.activities) {
    return redistributeActivityRefsShares(updatedStorey, ...ids);
  }
  return updatedStorey;
};

/**
 * Helper to mutate a storey by adding/removing activity references
 */
export const mutateStoreyActivityReference = {
  add: addStoreyActivityReference,
  remove: removeStoreyActivityReferences,
  update: updateStoreyActivityReference,
} as const;
